/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { defineMessages } from 'react-intl';

export const Phone = defineMessages({
  contactCenter: {
    id: 'phone.contactCenter',
    defaultMessage: 'Contact center'
  },
  phoneNumber: {
    id: 'phone.phoneNumber',
    defaultMessage: 'Phone number'
  },
  liveAgent: {
    id: 'phone.liveAgent',
    defaultMessage: 'Live agent'
  },
  advancedOptions: {
    id: 'phone.AdvancedOptions',
    defaultMessage: 'Advanced options'
  },
  gateway: {
    id: 'phone.gateway',
    defaultMessage: 'Gateway info'
  }
});

export const Schema = defineMessages({
  phoneNumberInvalid: {
    id: 'phone.Schema.phoneNumberInvalid',
    defaultMessage:
      'Phone numbers must be between 4 and 15 digits long and can only contain number characters (0-9) and plus(+), minus(-) and parenthesis.'
  },
  phoneDescriptionInvalid: {
    id: 'phone.Schema.phoneDescriptionInvalid',
    defaultMessage:
      'Description is too long. The maximum length is 128 characters.'
  },
  sttInstanceRequired: {
    id: 'phone.Schema.sttInstanceRequired',
    defaultMessage: 'A Speech to Text instance is required.'
  },
  sttModelRequired: {
    id: 'phone.Schema.sttInstanceRequired',
    defaultMessage: 'Speech to Text language model is required.'
  },
  ttsInstanceRequired: {
    id: 'phone.Schema.ttsInstanceRequired',
    defaultMessage: 'A Text to Speech instance is required.'
  },
  contactCenterRequired: {
    id: 'phone.Schema.contactCenterRequired',
    defaultMessage: 'A contact center is required.'
  },
  contactCenterIdRequired: {
    id: 'phone.Schema.contactCenterIdRequired',
    defaultMessage: 'A contact center ID is required.'
  },
  contactCenterAccessKeyIdRequired: {
    id: 'phone.Schema.contactCenterAccessKeyIdRequired',
    defaultMessage: 'An access key ID is required.'
  },
  contactCenterAccessKeySecretRequired: {
    id: 'phone.Schema.contactCenterAccessKeySecretRequired',
    defaultMessage: 'An access key secret is required.'
  },
  contactCenterArnRequired: {
    id: 'phone.Schema.contactCenterArnRequired',
    defaultMessage:
      'ARN must match: arn:aws:connect:<region>:<accountId>:instance/<instanceId>'
  },
  providerPathFromContactCenterConfig: {
    id: 'phone.Schema.providerPathFromContactCenterConfig',
    defaultMessage: 'provider'
  },
  sipAuthUsernameRequired: {
    id: 'phone.Schema.sipAuthUsernameRequired',
    defaultMessage: 'A SIP authentication username is required.'
  },
  sipAuthPasswordRequired: {
    id: 'phone.Schema.sipAuthPasswordRequired',
    defaultMessage: 'A SIP authentication password is required.'
  },
  genesysAudioConnectorAPIKeyRequired: {
    id: 'phone.Schema.genesysAudioConnectorAPIKeyRequired',
    defaultMessage: 'API key is required.'
  },
  genesysAudioConnectorClientSecretRequired: {
    id: 'phone.Schema.genesysAudioConnectorClientSecretRequired',
    defaultMessage: 'Client secret is required.'
  },
  genesysAudioConnectorConnectURIRequired: {
    id: 'phone.Schema.genesysAudioConnectorConnectURIRequired',
    defaultMessage: 'Connect URI is required.'
  }
});

export const CustomDeployment = defineMessages({
  title: {
    id: 'phone.CustomDeployment.title',
    defaultMessage: 'Custom Deployment'
  },
  description: {
    id: 'phone.CustomDeployment.description',
    defaultMessage:
      'Contact us to get in touch with implementation experts to guide you through the optimal way to set up your phone integration.'
  },
  listHeader: {
    id: 'phone.CustomDeployment.listHeader',
    defaultMessage: 'When you might need this:'
  },
  listItemOne: {
    id: 'phone.CustomDeployment.listItem1',
    defaultMessage:
      'On premium solutions that connect via DirectLink, AVPN, or MLPS'
  },
  listItemTwo: {
    id: 'phone.CustomDeployment.listItem2',
    defaultMessage:
      'Avoiding transfers of the PSTN to streamline your architecture'
  },
  listItemThree: {
    id: 'phone.CustomDeployment.listItem3',
    defaultMessage: 'Modernizing your entire contact center solution'
  },
  subtitle: {
    id: 'phone.CustomDeployment.subtitle',
    defaultMessage: 'Contact us'
  },
  subtitleDescription: {
    id: 'phone.CustomDeployment.subtitleDescription',
    defaultMessage:
      'This information will help us get you in touch with team members with the right expertise.'
  },
  fieldOneLabel: {
    id: 'phone.CustomDeployment.fieldOneLabel',
    defaultMessage: 'First name'
  },
  fieldTwoLabel: {
    id: 'phone.CustomDeployment.fieldTwoLabel',
    defaultMessage: 'Email Address'
  },
  fieldThreeLabel: {
    id: 'phone.CustomDeployment.fieldThreeLabel',
    defaultMessage: 'Additional requirements'
  },
  fieldThreePlaceholder: {
    id: 'phone.CustomDeployment.fieldThreePlaceholder',
    defaultMessage:
      'Please describe your existing contact center solution and any connectivity requirements'
  },
  note: {
    id: 'phone.PhoneNumber.note',
    defaultMessage:
      'Note: after you submit, expect to be contacted via email within _.'
  }
});

export const Speech = defineMessages({
  text: {
    id: 'phone.Speech.text',
    defaultMessage: 'text'
  },
  name: {
    id: 'phone.Speech.name',
    defaultMessage: 'Name'
  },
  plan: {
    id: 'phone.Speech.plan',
    defaultMessage: 'Plan'
  },
  resourceGroup: {
    id: 'phone.Speech.resourceGroup',
    defaultMessage: 'Resource Group'
  },
  account: {
    id: 'phone.Speech.account',
    defaultMessage: 'Account'
  },
  region: {
    id: 'phone.Speech.region',
    defaultMessage: 'Region'
  },
  customizable: {
    id: 'phone.Speech.customizable',
    defaultMessage: 'Customizable'
  },
  filtered: {
    id: 'phone.Speech.filtered',
    defaultMessage: 'Filtered'
  },
  filteredHelper: {
    id: 'phone.Speech.filteredHelper',
    defaultMessage: 'Selections filtered based on skill language'
  },
  createButtonLabel: {
    id: 'phone.Speech.createButtonLabel',
    defaultMessage: 'Create new instance'
  },
  entDataIsolWarning: {
    id: 'phone.Speech.entDataIsolWarning',
    defaultMessage:
      'In order to maintain data isolation, you must use Premium Speech services.'
  }
});
